import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Mic,
  MicOff,
  Volume2,
  VolumeX,
  Heart,
  Eye,
  EyeOff,
  X,
  Users,
  MessageCircle,
  Crown,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { useVoiceDiscovery } from "@/hooks/useVoiceDiscovery";
import { useAuthContext } from "@/contexts/AuthContext";
import { toast } from "sonner";

interface VoiceDiscoveryRoomProps {
  roomId: string;
  onLeave: () => void;
}

export const VoiceDiscoveryRoom = ({ roomId, onLeave }: VoiceDiscoveryRoomProps) => {
  const { user } = useAuthContext();
  const {
    currentRoom,
    participants,
    leaveRoom,
    toggleSpeaking,
    requestReveal,
    revealPhoto,
    sendVoiceLike,
    hasLiked,
    hasLikedMe,
    isMutualMatch,
  } = useVoiceDiscovery(roomId);

  const [isMuted, setIsMuted] = useState(true);
  const [showRevealConfirm, setShowRevealConfirm] = useState(false);

  // Handle leaving room
  const handleLeave = async () => {
    await leaveRoom(roomId);
    onLeave();
  };

  // Toggle mute
  const handleToggleMute = () => {
    const newMuted = !isMuted;
    setIsMuted(newMuted);
    toggleSpeaking(!newMuted);
  };

  // Handle photo reveal
  const handleRevealPhoto = () => {
    setShowRevealConfirm(false);
    revealPhoto();
  };

  // Current user participant
  const myParticipant = participants.find((p) => p.user_id === user?.id);
  const otherParticipants = participants.filter((p) => p.user_id !== user?.id);

  if (!currentRoom) {
    return (
      <div className="fixed inset-0 z-50 bg-background flex items-center justify-center">
        <div className="animate-pulse">Loading room...</div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-background overflow-hidden"
    >
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-pink-500/5 to-transparent" />

      {/* Header */}
      <header className="relative z-10 flex items-center justify-between px-4 py-3 border-b border-border/50 bg-background/80 backdrop-blur-sm">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-primary/20">
            <Volume2 className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="font-semibold text-foreground">{currentRoom.name}</h1>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Users className="w-3 h-3" />
              <span>{participants.length} people</span>
              {currentRoom.topic && (
                <>
                  <span>•</span>
                  <span>{currentRoom.topic}</span>
                </>
              )}
            </div>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={handleLeave}>
          Leave
        </Button>
      </header>

      {/* Main content - Participants grid */}
      <main className="relative z-10 flex-1 overflow-y-auto p-4 pb-32">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {/* My card first */}
          {myParticipant && (
            <motion.div
              layout
              className="relative aspect-square rounded-2xl bg-card border-2 border-primary/50 overflow-hidden"
            >
              <div className="absolute inset-0 flex flex-col items-center justify-center p-4">
                <div className="relative">
                  <Avatar className="w-20 h-20 border-4 border-background shadow-lg">
                    {myParticipant.has_revealed_photo ? (
                      <AvatarImage src={myParticipant.profile?.photo_url || undefined} />
                    ) : null}
                    <AvatarFallback className="text-2xl bg-gradient-to-br from-primary to-pink-500">
                      {myParticipant.profile?.name?.[0]}
                    </AvatarFallback>
                  </Avatar>
                  {!isMuted && (
                    <motion.div
                      animate={{ scale: [1, 1.3, 1] }}
                      transition={{ repeat: Infinity, duration: 1 }}
                      className="absolute -bottom-1 -right-1 p-1.5 rounded-full bg-green-500 text-white"
                    >
                      <Mic className="w-3 h-3" />
                    </motion.div>
                  )}
                </div>
                <p className="mt-3 font-semibold text-foreground text-center truncate w-full">
                  You
                </p>
                <span className="text-xs text-primary">That's you!</span>
              </div>

              {/* Reveal status */}
              <div className="absolute top-2 right-2">
                {myParticipant.has_revealed_photo ? (
                  <span className="px-2 py-1 rounded-full bg-green-500/20 text-green-500 text-[10px]">
                    <Eye className="w-3 h-3 inline mr-1" />
                    Visible
                  </span>
                ) : (
                  <span className="px-2 py-1 rounded-full bg-muted text-muted-foreground text-[10px]">
                    <EyeOff className="w-3 h-3 inline mr-1" />
                    Hidden
                  </span>
                )}
              </div>
            </motion.div>
          )}

          {/* Other participants */}
          {otherParticipants.map((participant) => {
            const isLiked = hasLiked(participant.user_id);
            const theyLikedMe = hasLikedMe(participant.user_id);
            const isMutual = isMutualMatch(participant.user_id);
            const revealRequestedByMe = participant.reveal_requested_by?.includes(user?.id || "");

            return (
              <motion.div
                key={participant.id}
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className={cn(
                  "relative aspect-square rounded-2xl bg-card border-2 overflow-hidden transition-all",
                  isMutual && "border-pink-500 ring-2 ring-pink-500/30",
                  participant.is_speaking && "border-green-500 ring-2 ring-green-500/30",
                  !isMutual && !participant.is_speaking && "border-border/50"
                )}
              >
                <div className="absolute inset-0 flex flex-col items-center justify-center p-4">
                  <div className="relative">
                    <Avatar className="w-20 h-20 border-4 border-background shadow-lg">
                      {participant.has_revealed_photo ? (
                        <AvatarImage src={participant.profile?.photo_url || undefined} />
                      ) : null}
                      <AvatarFallback className="text-2xl bg-gradient-to-br from-muted to-muted-foreground/20">
                        {participant.profile?.name?.[0]}
                      </AvatarFallback>
                    </Avatar>
                    {participant.is_speaking && (
                      <motion.div
                        animate={{ scale: [1, 1.3, 1] }}
                        transition={{ repeat: Infinity, duration: 1 }}
                        className="absolute -bottom-1 -right-1 p-1.5 rounded-full bg-green-500 text-white"
                      >
                        <Volume2 className="w-3 h-3" />
                      </motion.div>
                    )}
                  </div>
                  <p className="mt-3 font-semibold text-foreground text-center truncate w-full">
                    {participant.profile?.name || "Anonymous"}
                  </p>

                  {/* Status badges */}
                  <div className="flex items-center gap-1 mt-1">
                    {theyLikedMe && !isMutual && (
                      <span className="text-[10px] text-pink-500">Likes your vibe!</span>
                    )}
                    {isMutual && (
                      <span className="text-[10px] text-pink-500 flex items-center gap-1">
                        <Sparkles className="w-3 h-3" />
                        Mutual match!
                      </span>
                    )}
                  </div>
                </div>

                {/* Photo visibility indicator */}
                <div className="absolute top-2 right-2">
                  {participant.has_revealed_photo ? (
                    <span className="px-2 py-1 rounded-full bg-green-500/20 text-green-500 text-[10px]">
                      <Eye className="w-3 h-3 inline" />
                    </span>
                  ) : (
                    <span className="px-2 py-1 rounded-full bg-muted text-muted-foreground text-[10px]">
                      <EyeOff className="w-3 h-3 inline" />
                    </span>
                  )}
                </div>

                {/* Action buttons */}
                <div className="absolute bottom-2 left-2 right-2 flex gap-2">
                  {!isLiked && (
                    <Button
                      variant="secondary"
                      size="sm"
                      className="flex-1 h-8 text-xs gap-1"
                      onClick={() => sendVoiceLike(participant.user_id)}
                    >
                      <Heart className="w-3 h-3" />
                      Like Vibe
                    </Button>
                  )}
                  {isLiked && !isMutual && (
                    <Button variant="outline" size="sm" className="flex-1 h-8 text-xs" disabled>
                      <Heart className="w-3 h-3 text-pink-500 fill-pink-500 mr-1" />
                      Liked
                    </Button>
                  )}
                  {isMutual && (
                    <Button variant="default" size="sm" className="flex-1 h-8 text-xs gap-1">
                      <MessageCircle className="w-3 h-3" />
                      Chat
                    </Button>
                  )}
                  {!participant.has_revealed_photo && !revealRequestedByMe && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => requestReveal(participant.user_id)}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </motion.div>
            );
          })}

          {/* Empty state */}
          {otherParticipants.length === 0 && (
            <div className="col-span-full text-center py-12">
              <Users className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">
                Waiting for others to join...
              </p>
            </div>
          )}
        </div>
      </main>

      {/* Bottom controls */}
      <div className="absolute bottom-0 left-0 right-0 z-20 p-4 bg-gradient-to-t from-background via-background to-transparent">
        <div className="max-w-md mx-auto flex items-center justify-center gap-4">
          {/* Mute toggle */}
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={handleToggleMute}
            className={cn(
              "w-16 h-16 rounded-full flex items-center justify-center shadow-lg transition-colors",
              isMuted
                ? "bg-muted text-muted-foreground"
                : "bg-green-500 text-white"
            )}
          >
            {isMuted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
          </motion.button>

          {/* Reveal photo button */}
          {myParticipant && !myParticipant.has_revealed_photo && (
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => setShowRevealConfirm(true)}
            >
              <Eye className="w-4 h-4" />
              Reveal Photo
            </Button>
          )}

          {/* Leave button */}
          <Button variant="destructive" size="icon" className="w-12 h-12 rounded-full" onClick={handleLeave}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        <p className="text-center text-xs text-muted-foreground mt-3">
          {isMuted ? "Tap the mic to speak" : "You're live! Others can hear you"}
        </p>
      </div>

      {/* Reveal confirmation modal */}
      <AnimatePresence>
        {showRevealConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
            onClick={() => setShowRevealConfirm(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-card rounded-2xl p-6 max-w-sm w-full text-center"
            >
              <Eye className="w-12 h-12 mx-auto text-primary mb-4" />
              <h3 className="text-lg font-semibold mb-2">Reveal Your Photo?</h3>
              <p className="text-sm text-muted-foreground mb-6">
                Others in this room will be able to see your profile photo. This can't be undone for this session.
              </p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowRevealConfirm(false)}
                >
                  Keep Hidden
                </Button>
                <Button className="flex-1" onClick={handleRevealPhoto}>
                  Reveal Photo
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
